/* Companion products offered after the extras step (config.crossSell): a short
   list of things that ship in the same box, each at a discount, added without
   leaving the flow. Deliberately AFTER the extras step — asking earlier
   competes with the product they came for.

   Built as a LIST from the start even though only one product is offered
   today: adding the next one is a metafield edit, no code. Each card carries
   its own personalization (the same property keys its own product page
   writes) and its own details popup with the full gallery.

   Prices here are display only; the automatic discount in Shopify is what
   charges less. Both read the same percent from the config, so the promise on
   the card and the receipt at checkout cannot disagree.

   Markup lives in personalizer-crosssell-render.js. */
(function () {
  'use strict';

  var ctx = null;
  var head = null;
  var offers = [];
  var state = {};      // handle -> { on, variantId, values }
  var built = false;
  var dialog = null;

  function fmt(cents) {
    return window.PersonalizerCart.formatMoney(cents, ctx.config.moneyFormat);
  }
  function off(cents, pctVal) { return Math.round(cents * (100 - pctVal) / 100); }

  function stock(o) {
    return (o.variants || []).filter(function (v) { return v.available; });
  }
  function chosen(o) {
    var st = state[o.handle];
    var list = stock(o);
    // matchCount: preselect the variant whose title leads with the order's
    // head-count ("1 Person" for one pet), first variant if none matches —
    // resolved lazily so a restored session's count is the one used
    if (!st.variantId && o.matchCount && list.length > 1 && ctx && ctx.state) {
      var n = (ctx.state.besties || []).length;
      var m = list.filter(function (v) { return parseInt(v.title, 10) === n; })[0] || list[0];
      if (m) st.variantId = m.id;
    }
    for (var i = 0; i < list.length; i++) if (list[i].id === st.variantId) return list[i];
    return null;
  }
  /* Before a size is picked the card quotes the cheapest one as a "from"
     price — never the dearest, which reads as bait once they choose. */
  function fullCents(o) {
    var v = chosen(o);
    if (v) return v.price;
    var min = 0;
    stock(o).forEach(function (x) { if (!min || x.price < min) min = x.price; });
    return min;
  }

  /* One flat object per offer for the renderer: no lookups, no maths there. */
  function view(o) {
    var st = state[o.handle];
    var pctVal = (o.discountPct == null) ? 30 : o.discountPct; // 0 = no discount, honestly
    var full = fullCents(o);
    return {
      id: o.handle,
      title: o.title,
      rowText: o.rowText,
      detailsLabel: o.detailsLabel || 'Details',
      url: o.url,
      summary: o.summary,
      usps: o.usps || [],
      fields: o.fields || [],
      optionLabel: o.optionLabel || 'Size',
      images: o.images || [],
      image: (o.images || [])[0],
      photoNote: o.usePhotos
        ? (o.photoNote || 'Made from the photos you just uploaded — nothing else to send.')
        : '',
      discountPct: pctVal,
      fullCents: full,
      nowCents: off(full, pctVal),
      prefix: (!chosen(o) && stock(o).length > 1) ? 'from' : '',
      variants: stock(o).map(function (v) {
        return { id: v.id, title: v.title, price: v.price, compare: v.compare || 0, nowCents: off(v.price, pctVal) };
      }),
      photoOnlyNew: !!o.photoOnlyNew,
      photoLater: o.photoLater || null,
      later: !!st.later,
      variantId: st.variantId,
      values: st.values,
      on: st.on,
      photoMode: st.photoMode,
      usePhotos: !!o.usePhotos,
      photoPick: pickView(o),
      sizeHint: o.sizeHint || '',
    };
  }

  /* The order's photos, and how many of them this piece can account for. */
  function orderPhotos() {
    var g = ctx && ctx.photoGroup && ctx.photoGroup();
    return g ? g.getPhotos().filter(function (p) { return p && p.url; }) : [];
  }

  function portraitCount(o) {
    var v = chosen(o);
    var m = v && /(\d+)/.exec(v.title || '');
    return m ? parseInt(m[1], 10) : 0;
  }

  /* The photos on the order, always shown while reusing them, always
     multi-select: several angles of one pet is a normal way to brief a single
     portrait, so the number of photos says nothing about the number of faces.

     Everything is preselected — except when there are more photos than
     portraits, which is the one case where "all of them" cannot be what they
     mean, so it starts empty and asks. */
  function pickView(o) {
    var st = state[o.handle];
    if (!o.usePhotos || st.photoMode !== 'reuse') return null;
    var photos = orderPhotos();
    if (!photos.length) return null;

    var n = portraitCount(o);
    var urls = photos.map(function (p) { return p.url; });
    var mustAsk = !!n && photos.length > n;

    var chosenUrls;
    if (st.photoPick === null) {
      // untouched: everything unless the counts force a decision
      chosenUrls = mustAsk ? [] : urls.slice();
    } else {
      chosenUrls = st.photoPick.filter(function (u) { return urls.indexOf(u) !== -1; });
    }

    return {
      photos: photos,
      chosen: chosenUrls,
      mustAsk: mustAsk,
      question: mustAsk
        ? (n === 1 ? 'Which photo is this one for?' : 'Which photos is this one for?')
        : 'Photos we will use',
      hint: mustAsk
        ? 'You are making ' + n + (n === 1 ? ' portrait' : ' portraits')
          + ' — tick the ones it should show. Several angles of the same face is fine.'
        : 'Untick any that are not for this piece.',
    };
  }

  function offerFor(handle) {
    for (var i = 0; i < offers.length; i++) if (offers[i].handle === handle) return offers[i];
    return null;
  }

  /* --- what the rest of the personalizer asks this module --- */

  function hasOffer() { return offers.length > 0; }

  /* Offers built from the order's own photos need those photo properties copied
     onto their cart line. The cart module owns the photo list, so it asks. */
  function usesOrderPhotos(handle) {
    var o = offerFor(handle);
    return !!(o && o.usePhotos && state[handle] && state[handle].photoMode !== 'new');
  }

  /* Which of the order's photos belong on this offer's line: the chosen subset
     when one was needed, otherwise all of them. */
  function orderPhotosFor(handle, photos) {
    var o = offerFor(handle);
    var pick = o && pickView(o);
    if (!pick) return photos;
    // whatever is ticked on screen is what the line carries
    return photos.filter(function (p) { return pick.chosen.indexOf(p.url) !== -1; });
  }
  function acceptedOffers() {
    return offers.filter(function (o) { return state[o.handle].on; });
  }
  function accepted() { return acceptedOffers().length > 0; }

  function cents() {
    return acceptedOffers().reduce(function (sum, o) {
      return sum + off(fullCents(o), (o.discountPct == null) ? 30 : o.discountPct);
    }, 0);
  }
  function compareCents() {
    return acceptedOffers().reduce(function (sum, o) { return sum + fullCents(o); }, 0);
  }

  /* First gap on an accepted card, named so the customer knows which one. */
  /* scope 'modal' checks only what the popup actually renders. The photo
     controls are card-only, so holding the popup shut over them would be an
     unanswerable question — which is exactly what it became. */
  function gapFor(o, scope) {
    if (!state[o.handle].on) return null;
    // card titles carry a selling line after an em dash; error copy wants the
    // product's name, not the pitch
    var short = String(o.title || '').split('—')[0].trim();
    if (stock(o).length > 1 && !chosen(o)) {
      return o.sizePrompt || ('Pick a ' + String(o.optionLabel || 'size').toLowerCase() + ' for the ' + short + '.');
    }
    var st = state[o.handle];
    if (scope !== 'modal') {
      var pick = pickView(o);
      if (pick && !pick.chosen.length) {
        return pick.mustAsk ? pick.question : 'Pick at least one photo for this piece.';
      }
      if (st.photoMode === 'new' && !st.later && (!st.photos || !st.photos.getPhotos().length)) {
        return 'Add a photo for the ' + short
          + (o.photoOnlyNew ? (o.photoLater ? ', or tick "' + (o.photoLater.label || 'send it later') + '".' : '.') : ', or switch back to your order photos.');
      }
    }
    var fields = o.fields || [];
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (!f.required) continue;
      if (!String(state[o.handle].values[f.key] || '').trim()) {
        return (f.type === 'swatch' ? 'Pick a ' + String(f.label || 'option').toLowerCase() : 'Fill in "' + f.label + '"')
          + ' for the ' + short + '.';
      }
    }
    return null;
  }
  function firstGap() {
    for (var i = 0; i < offers.length; i++) {
      var g = gapFor(offers[i]);
      if (g) return { offer: offers[i], message: g };
    }
    return null;
  }
  function missing() {
    var g = firstGap();
    return g ? g.message : null;
  }
  function isValid() { return !firstGap(); }

  /* Cart lines. Property KEYS mirror what each product's own page writes, so
     fulfillment reads one order the same way whichever door it came through. */
  function items() {
    var out = [];
    acceptedOffers().forEach(function (o) {
      var v = chosen(o) || (stock(o).length === 1 ? stock(o)[0] : null);
      if (!v) return;
      var props = {};
      var st = state[o.handle];
      if (st.photoMode === 'new' && st.photos) {
        var own = st.photos.getPhotos();
        own.forEach(function (p, i) { props['YOUR PHOTO_' + (i + 1)] = p.url; });
        if (own.length) props['Made from'] = 'the photos uploaded for this piece';
      }
      if (st.later && !(st.photos && st.photos.getPhotos().length)) props['Photo'] = 'Customer will send it later';
      (o.fields || []).forEach(function (f) {
        var val = String(state[o.handle].values[f.key] || '').trim();
        if (val) props[f.key] = val.slice(0, f.max || 200);
      });
      out.push({ id: v.id, quantity: 1, properties: props, offerHandle: o.handle });
    });
    return out;
  }

  /* --- DOM sync: build once, then only classes and text change, so a text
     field can never be rebuilt out from under someone typing --- */

  function cardEl(handle) {
    return ctx.root.querySelector('[data-pzx-card="' + handle + '"]');
  }

  function syncCard(o) {
    var v = view(o);
    var el = cardEl(o.handle);
    if (!el) return;
    el.classList.toggle('is-on', v.on);

    var mount = el.querySelector('[data-pzx-choices]');
    // built once: this block holds a live upload widget and text the customer
    // is typing into, neither of which survives a re-render
    if (v.on && !mount.firstChild) {
      mount.innerHTML = window.PersonalizerCrossRender.pickers(v, 'card', fmt, ctx.config.maxFileMb);
    }
    // a product with nothing to choose (one variant, no fields) must not draw
    // an empty picker area under itself
    mount.hidden = !v.on || !mount.firstChild;

    var row = el.querySelector('[data-pzx-toggle]');
    if (row) row.setAttribute('aria-pressed', v.on ? 'true' : 'false');
    var price = el.querySelector('.pzx-price');
    if (price) price.outerHTML = window.PersonalizerCrossRender.priceHtml(v, fmt, true);

    syncPickers(o.handle);
    syncPick(o);
    if (v.on) ensureUploader(o.handle);
  }

  /* The "which photo" question changes with the portrait count and the photo
     mode, so its wrapper is re-rendered when the question itself changes — and
     only then. Ticks are a class toggle, so tapping a thumbnail never rebuilds
     the node the tap landed on. */
  function syncPick(o) {
    var wrap = ctx.root.querySelector('[data-pzx-pickwrap="' + o.handle + '"]');
    if (!wrap) return;
    var v = view(o);
    var sig = v.photoPick
      ? v.photoPick.question + '|' + v.photoPick.photos.map(function (p) { return p.url; }).join('|')
      : '';
    if (wrap.getAttribute('data-sig') !== sig) {
      wrap.innerHTML = v.photoPick ? window.PersonalizerCrossRender.photoPick(v) : '';
      wrap.setAttribute('data-sig', sig);
    }
    // read the COMPUTED selection: an untouched picker is preselected, and the
    // raw state is still null at that point
    var picked = v.photoPick ? v.photoPick.chosen : [];
    wrap.querySelectorAll('[data-pzx-photopick]').forEach(function (b) {
      var on = picked.indexOf(b.getAttribute('data-pzx-photopick')) !== -1;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /* The offer's own upload group is created the first time it is asked for and
     then left alone — rebuilding it would drop files already uploaded. */
  function ensureUploader(handle) {
    var st = state[handle];
    var host = ctx.root.querySelector('[data-pzx-photos="' + handle + '"]');
    if (!host) return;
    host.hidden = st.photoMode !== 'new';
    if (st.photoMode !== 'new' || st.photos) return;
    var group = host.querySelector('[data-photo-group]');
    if (!group || !window.PersonalizerUpload) return;
    st.photos = window.PersonalizerUpload.initPhotoGroup(group, {
      uploadEndpoint: ctx.config.uploadEndpoint,
      getMaxPhotos: function () { return ctx.config.maxPhotos || 10; },
      maxFileMb: ctx.config.maxFileMb,
      requirePhotos: true,
      restored: [],
      onChange: function () {
        showError(null);
        window.PersonalizerRender.updateBar(ctx);
      },
    });
  }

  function syncPickers(handle) {
    var o = offerFor(handle);
    var st = state[handle];
    document.querySelectorAll('[data-pzx-size][data-pzx-item="' + handle + '"]').forEach(function (b) {
      var on = parseInt(b.getAttribute('data-pzx-size'), 10) === st.variantId;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-pzx-photomode][data-pzx-item="' + handle + '"]').forEach(function (b) {
      var on = b.getAttribute('data-pzx-photomode') === st.photoMode;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-pzx-later][data-pzx-item="' + handle + '"]').forEach(function (c) {
      c.checked = !!st.later;
    });
    document.querySelectorAll('[data-pzx-val][data-pzx-item="' + handle + '"]').forEach(function (b) {
      var on = st.values[b.getAttribute('data-pzx-field')] === b.getAttribute('data-pzx-val');
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      // the chosen name rides next to the label, so a collapsed swatch row
      // still says what was picked
      if (on) {
        var field = b.closest('.pzx-field');
        var label = field && field.querySelector('.pzx-field__label b');
        if (label) label.textContent = b.getAttribute('data-pzx-val');
      }
    });
    if (dialog && dialog.getAttribute('data-pzx-for') === handle) {
      var mAdd = dialog.querySelector('[data-pzx-modal-add]');
      if (mAdd) {
        var v = view(o);
        mAdd.innerHTML = v.on ? '✓ Added — back to my order' : 'Add to my order — ' + fmt(v.nowCents);
        mAdd.classList.toggle('is-on', v.on);
      }
      var mPrice = dialog.querySelector('.pzx-price');
      if (mPrice) mPrice.outerHTML = window.PersonalizerCrossRender.priceHtml(view(o), fmt, true);
    }
  }

  function syncAll() {
    offers.forEach(syncCard);
    showError(null);
    window.PersonalizerRender.updateBar(ctx);
  }

  /* Errors land on the card they belong to (and in the popup when it is open),
     never as one anonymous line at the bottom of the list. */
  function showError(msg, handle) {
    ctx.root.querySelectorAll('[data-pzx-err]').forEach(function (el) {
      var mine = handle && el.getAttribute('data-pzx-err') === handle;
      el.hidden = !(msg && mine);
      el.textContent = mine && msg ? msg : '';
    });
    var mErr = dialog && dialog.querySelector('[data-pzx-err-modal]');
    if (mErr) {
      var forThis = msg && handle && dialog.getAttribute('data-pzx-for') === handle;
      mErr.hidden = !forThis;
      mErr.textContent = forThis ? msg : '';
    }
    if (msg && handle) {
      var el = cardEl(handle);
      if (el && !modalOpen()) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }
  function modalOpen() { return !!(dialog && dialog.hasAttribute('open')); }

  /* --- details popup: relocated to <body> on open, because the product page
     has transformed ancestors that would break position:fixed --- */

  function openModal(handle) {
    var o = offerFor(handle);
    if (!o) return;
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'pzx-modal';
      dialog.setAttribute('data-pzx-modal', ''); // hook for styling and tests
      dialog.addEventListener('click', function (e) { if (e.target === dialog) closeModal(); });
      dialog.addEventListener('cancel', function (e) { e.preventDefault(); closeModal(); });
      ctx.root.appendChild(dialog);
    }
    dialog.setAttribute('data-pzx-for', handle);
    dialog.innerHTML = window.PersonalizerCrossRender.modal(view(o), fmt);
  var strip = dialog.querySelector('[data-pzx-slides]');
  if (strip) {
    var settle;
    strip.addEventListener('scroll', function () {
      clearTimeout(settle);
      settle = setTimeout(function () { markSlide(currentSlide()); }, 90);
    }, { passive: true });
  }
    if (dialog.parentNode !== document.body) document.body.appendChild(dialog);
    try {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } catch (err) {
      dialog.setAttribute('open', '');
    }
    var x = dialog.querySelector('[data-pzx-close]');
    if (x) x.focus();
  }

  /* The gallery is a scroll-snapping strip: swiping is the browser's job, and
     the arrows and thumbnails just scroll it to the right frame. */
  function slidesEl() { return dialog && dialog.querySelector('[data-pzx-slides]'); }

  function currentSlide() {
    var strip = slidesEl();
    if (!strip || !strip.clientWidth) return 0;
    return Math.round(strip.scrollLeft / strip.clientWidth);
  }

  function slideTo(i) {
    var strip = slidesEl();
    if (!strip) return;
    var last = strip.children.length - 1;
    var target = Math.max(0, Math.min(last, i));
    strip.scrollTo({ left: target * strip.clientWidth, behavior: 'smooth' });
    markSlide(target);
  }

  function markSlide(i) {
    if (!dialog) return;
    dialog.querySelectorAll('[data-pzx-thumb]').forEach(function (b) {
      b.classList.toggle('is-on', parseInt(b.getAttribute('data-pzx-thumb'), 10) === i);
    });
  }

  function closeModal() {
    if (!dialog) return;
    try {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    } catch (err) {
      dialog.removeAttribute('open');
    }
    dialog.removeAttribute('data-pzx-for');
    var back = ctx.root.querySelector('[data-pzx-open]');
    if (back) back.focus();
  }

  /* --- events: delegated once, so rebuilt pickers can never lose them --- */

  function onClick(e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var el = function (sel) { return t.closest(sel); };
    var node = el('[data-pzx-item]');
    var handle = node && node.getAttribute('data-pzx-item');
    var o = handle ? offerFor(handle) : null;

    if (el('[data-pzx-close]')) { closeModal(); return; }
    if (el('[data-pzx-open]') && o) { openModal(handle); return; }

    var thumb = el('[data-pzx-thumb]');
    if (thumb && dialog) {
      slideTo(parseInt(thumb.getAttribute('data-pzx-thumb'), 10) || 0);
      return;
    }

    var nav = el('[data-pzx-slidenav]');
    if (nav && dialog) {
      slideTo(currentSlide() + (parseInt(nav.getAttribute('data-pzx-slidenav'), 10) || 1));
      return;
    }

    if (!o) {
      if (el('[data-pzx-decline]')) {
        offers.forEach(function (x) { state[x.handle].on = false; });
        syncAll();
        ctx.root.dispatchEvent(new CustomEvent('personalizer:submit', { bubbles: true }));
      }
      return;
    }

    var size = el('[data-pzx-size]');
    if (size) {
      state[handle].variantId = parseInt(size.getAttribute('data-pzx-size'), 10);
      state[handle].touched = true; // an explicit pick outranks the auto-match
      state[handle].on = true; // picking an option IS saying yes
      syncCard(o);
      showError(null);
      window.PersonalizerRender.updateBar(ctx);
      return;
    }

    if (el('[data-pzx-pickall]')) {
      var everything = orderPhotos().map(function (p) { return p.url; });
      state[handle].photoPick = everything;
      syncCard(o);
      showError(null);
      return;
    }

    var pickBtn = el('[data-pzx-photopick]');
    if (pickBtn) {
      var url = pickBtn.getAttribute('data-pzx-photopick');
      var pick = pickView(o);
      // start from what is on screen, so the first tap edits the preselection
      // rather than throwing it away
      var list = (state[handle].photoPick === null ? (pick ? pick.chosen : []) : state[handle].photoPick).slice();
      var at = list.indexOf(url);
      if (at !== -1) list.splice(at, 1);
      else list.push(url);
      state[handle].photoPick = list;
      syncCard(o);
      showError(null);
      return;
    }

    var later = el('[data-pzx-later]');
    if (later) {
      state[handle].later = !!later.checked;
      state[handle].on = true;
      syncCard(o);
      showError(null);
      window.PersonalizerRender.updateBar(ctx);
      return;
    }

    var mode = el('[data-pzx-photomode]');
    if (mode) {
      state[handle].photoMode = mode.getAttribute('data-pzx-photomode');
      state[handle].on = true;
      syncCard(o);
      ensureUploader(handle);
      showError(null);
      window.PersonalizerRender.updateBar(ctx);
      return;
    }

    var sw = el('[data-pzx-val]');
    if (sw) {
      state[handle].values[sw.getAttribute('data-pzx-field')] = sw.getAttribute('data-pzx-val');
      state[handle].on = true;
      syncCard(o);
      showError(null);
      window.PersonalizerRender.updateBar(ctx);
      return;
    }

    if (el('[data-pzx-toggle]')) {
      state[handle].on = !state[handle].on;
      syncCard(o);
      showError(null);
      window.PersonalizerRender.updateBar(ctx);
      return;
    }

    if (el('[data-pzx-modal-add]')) {
      state[handle].on = true;
      syncCard(o);
      window.PersonalizerRender.updateBar(ctx);
      // only what the popup shows can hold it open
      var gap = gapFor(o, 'modal');
      if (gap) { showError(gap, handle); return; }
      closeModal();
      // anything still missing is asked on the card, which is now in front of
      // them — showError scrolls it into view once the popup is out of the way
      var rest = gapFor(o);
      if (rest) showError(rest, handle);
    }
  }

  /* role="button" on a non-button element: Enter and Space have to be wired
     by hand or the row is unusable from a keyboard. */
  function onKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var row = t.closest('[data-pzx-toggle]');
    if (!row || t.closest('[data-pzx-open]')) return;
    e.preventDefault();
    row.click();
  }

  function onInput(e) {
    var el = e.target;
    if (!el.matches || !el.matches('input[data-pzx-field], textarea[data-pzx-field]')) return;
    var handle = el.getAttribute('data-pzx-item');
    var key = el.getAttribute('data-pzx-field');
    if (!state[handle]) return;
    state[handle].values[key] = el.value;
    // mirror into the same field on the other surface without stealing focus
    document.querySelectorAll('[data-pzx-item="' + handle + '"][data-pzx-field="' + key + '"]')
      .forEach(function (other) { if (other !== el && other.value !== el.value) other.value = el.value; });
    showError(null);
  }

  /* --- lifecycle --- */

  function init(context) {
    ctx = context;
    var cfg = ctx.config.crossSell;
    if (!cfg) return;
    head = {
      heading: cfg.heading || 'One more thing 🎁',
      sub: cfg.sub || '',
      declineLabel: cfg.declineLabel || 'No thanks — just my order',
    };
    offers = (cfg.items || []).filter(function (o) { return o && stock(o).length; });
    offers.forEach(function (o) {
      state[o.handle] = {
        on: false,
        touched: false,
        variantId: stock(o).length === 1 ? stock(o)[0].id : null,
        values: {},
        // reuse the order's photos unless the offer says a person needs their own
        photoMode: (o.photoDefault === 'new' || o.photoOnlyNew) ? 'new' : 'reuse',
        later: false,          // "I'll send the photo later" tick
        photos: null,          // an own upload group, created the first time it is needed
        photoPick: null,       // null = untouched; an array once they choose
      };
    });
    if (!offers.length) return;
    document.addEventListener('click', onClick, false);
    document.addEventListener('input', onInput, false);
    document.addEventListener('keydown', onKeydown, false);
  }

  /* Called when the interstitial becomes visible. */
  function render(context) {
    ctx = context;
    if (!hasOffer()) return;
    var mount = ctx.root.querySelector('[data-personalizer-cross]');
    if (!mount) return;
    if (!built) {
      mount.innerHTML = window.PersonalizerCrossRender.list(head, offers.map(view), fmt);
      built = true;
    }
    mount.hidden = false;
    syncAll();
  }

  window.PersonalizerCross = {
    init: init,
    render: render,
    hasOffer: hasOffer,
    accepted: accepted,
    isValid: isValid,
    missing: missing,
    showError: function () {
      var g = firstGap();
      if (g) showError(g.message, g.offer.handle);
    },
    cents: cents,
    compareCents: compareCents,
    items: items,
    usesOrderPhotos: usesOrderPhotos,
    orderPhotosFor: orderPhotosFor,
  };
})();
